import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Alert, AlertTitle, Overlay, usePortalContainer, Button } from "@theorgchartdev/storybook";

type BannerAction = {
  label: string;
  customOnClick?: () => void | Promise<void>;
};

type BannerErrorAlertProps = {
  title?: string;
  message?: ReactNode;
  onClose: () => void;
  confirm?: BannerAction;
  deny?: BannerAction;
};

export const BannerErrorAlert = ({
  title,
  message,
  onClose,
  confirm,
  deny
}: BannerErrorAlertProps) => {
  const portalContainer = usePortalContainer();
  const [submittingAction, setSubmittingAction] = useState<"confirm" | "deny" | null>(null);

  const handleActionClick =
    (onClick: BannerAction["customOnClick"], actionKey: "confirm" | "deny") => async () => {
      setSubmittingAction(actionKey);
      try {
        await onClick?.();
      } catch (error) {
        console.error(error);
      } finally {
        setSubmittingAction(null);
        onClose();
      }
    };

  const actions =
    confirm && deny ? (
      <div className="oc-banner-error-alert-actions">
        <Button
          modifier="secondary"
          size="small"
          loading={submittingAction === "deny"}
          onClick={handleActionClick(deny.customOnClick, "deny")}
          dataTestId="banner-alert-deny-btn"
        >
          {deny.label}
        </Button>
        <Button
          modifier="primary"
          size="small"
          loading={submittingAction === "confirm"}
          onClick={handleActionClick(confirm.customOnClick, "confirm")}
          dataTestId="banner-alert-confirm-btn"
        >
          {confirm.label}
        </Button>
      </div>
    ) : null;

  const banner = (
    <Overlay
      show={true}
      className="oc-banner-error-alert-overlay"
      isModal={true}
      showOverlay={true}
      onClose={onClose}
      padding="0px"
    >
      <Alert severity="warning" alertActions={{ closeAction: onClose }}>
        {title ? <AlertTitle>{title}</AlertTitle> : null}
        {message}
        {actions}
      </Alert>
    </Overlay>
  );

  return createPortal(banner, portalContainer ?? document.body);
};

export default BannerErrorAlert;
